import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip, Box } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { DISCONTINUED_TOKENS } from './discontinued-service.tokens';
const DEFAULT_TOOLTIP = 'This service has been deleted. Past data is preserved for reference.';

/**
 * Visual indicator that a service tied to an appointment / lead / report row has been soft-deleted.
 *
 * Variants:
 *   size="md" (default) — outlined yellow chip with the label "Discontinued Service".
 *   size="sm"           — icon-only warning triangle (used in tightest surfaces such as month-view
 *                         appointment chips and inline leads-table cells).
 *
 * Tooltip copy matches CIN-5774 figma-brief.md §2.
 *
 * TODO design QA: the yellow palette below is taken literally from Figma
 * `light/yellow/600|700`. Project theme has no equivalent named token yet —
 * see /src/components/discontinued-service/discontinued-service.tokens.js.
 */
export const DiscontinuedServiceChip = ({
  size = 'md',
  withTooltip = true,
  tooltipText = DEFAULT_TOOLTIP,
  sx = {}
}) => {
  const chip = size === 'sm' ? <Box component="span" aria-label="Discontinued service" data-testid="discontinued-service-chip-sm" sx={{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: DISCONTINUED_TOKENS.deep,
    ...sx
  }}>
        <WarningAmberOutlinedIcon sx={{
      fontSize: 16
    }} />
      </Box> : <Chip size="small" variant="outlined" icon={<WarningAmberOutlinedIcon sx={{
    fontSize: 14
  }} />} label="Discontinued Service" data-testid="discontinued-service-chip" sx={{
    // typography/chip/label — 12px, 500, letter-spacing 0.16
    'height': 24,
    'fontSize': 12,
    'fontWeight': 500,
    'letterSpacing': 0.16,
    'borderColor': DISCONTINUED_TOKENS.accent,
    'color': DISCONTINUED_TOKENS.deep,
    'backgroundColor': 'transparent',
    '& .MuiChip-icon': {
      ml: 0.5,
      mr: -0.25,
      color: DISCONTINUED_TOKENS.deep
    },
    '& .MuiChip-label': {
      color: DISCONTINUED_TOKENS.deep
    },
    ...sx
  }} />;
  if (!withTooltip) return chip;
  return <Tooltip title={tooltipText} placement="top" arrow>
      <span>{chip}</span>
    </Tooltip>;
};
DiscontinuedServiceChip.propTypes = {
  size: PropTypes.oneOf(['sm', 'md']),
  withTooltip: PropTypes.bool,
  tooltipText: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  sx: PropTypes.object
};
export default DiscontinuedServiceChip;